import { ampli } from '@magicschool/ampli-client';
import { deepEquals } from '@magicschool/runes/core/equality/deepEquals';
import { toast } from '@magicschool/spellbook';
import type { Tables } from '@magicschool/supabase/types';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { ADMIN_USERS_SEARCH_LIMIT } from '@/features/admin/users/constants';
import type { AdminUserRow } from '@/features/admin/users/types';
import { storage } from '@/features/storage';
import { getAllCookies } from '@/util/cookies';

const showRouteMessage = async ({ response }: { response: Response }) => {
  const title = await response.text().catch(() => '');
  toast({ title: title || 'Failed to update the user role', status: 'error' });
};

export type UpdateUserRoleResponse = {
  user: Tables<'users'>;
};

export type AdminUsersLoadParams = {
  orgId?: string;
};

export type AdminUsersTextFilters = {
  idFilter: string;
  nameFilter: string;
  emailFilter: string;
  orgFilter: string;
};

export type AdminUsersArrayFilters = {
  planFilter: string[];
  roleFilter: string[];
  statusFilter: string[];
  institutionalRoleFilter: string[];
};

export type AdminUsersServerFilters = AdminUsersTextFilters & AdminUsersArrayFilters;

export const ADMIN_USERS_TEXT_FILTER_KEYS: (keyof AdminUsersTextFilters)[] = ['idFilter', 'nameFilter', 'emailFilter', 'orgFilter'];
export const ADMIN_USERS_ARRAY_FILTER_KEYS: (keyof AdminUsersArrayFilters)[] = [
  'planFilter',
  'roleFilter',
  'statusFilter',
  'institutionalRoleFilter',
];

export const EMPTY_SERVER_FILTERS: AdminUsersServerFilters = {
  idFilter: '',
  nameFilter: '',
  emailFilter: '',
  orgFilter: '',
  planFilter: [],
  roleFilter: [],
  statusFilter: [],
  institutionalRoleFilter: [],
};

const ALL_SERVER_FILTER_KEYS = [...ADMIN_USERS_TEXT_FILTER_KEYS, ...ADMIN_USERS_ARRAY_FILTER_KEYS] as const;

/** Pick only filter keys so lastFetchFilters is not a dump of the whole store slice. */
const pickServerFilters = (state: AdminUsersServerFilters): AdminUsersServerFilters => {
  const filters = {} as AdminUsersServerFilters;
  for (const key of ALL_SERVER_FILTER_KEYS) {
    filters[key] = state[key] as never;
  }
  return filters;
};

/** Skip refetch when last fetch was complete, deleted rows aren't newly requested, and every field is unchanged, a narrowing, or a new AND. */
export const canSkipServerRefetch = (
  lastFetchFilters: AdminUsersServerFilters,
  lastFetchWasComplete: boolean,
  nextFilters: AdminUsersServerFilters,
  lastFetchIncludedDeleted: boolean,
  nextIncludeDeleted: boolean,
): boolean => {
  if (!lastFetchWasComplete) return false;
  if (nextIncludeDeleted && !lastFetchIncludedDeleted) return false;

  const textOk = ADMIN_USERS_TEXT_FILTER_KEYS.every((key) => {
    const oldValue = lastFetchFilters[key];
    const newValue = nextFilters[key];
    if (!oldValue) return true;
    if (!newValue) return false;
    return newValue.toLowerCase().includes(oldValue.toLowerCase());
  });
  if (!textOk) return false;

  return ADMIN_USERS_ARRAY_FILTER_KEYS.every((key) => {
    const oldValues = lastFetchFilters[key];
    const newValues = nextFilters[key];
    if (oldValues.length === 0) return true;
    if (newValues.length === 0) return false;
    return newValues.every((value) => oldValues.includes(value));
  });
};

export type AdminUsersStore = AdminUsersServerFilters & {
  orgId?: string;
  loading: boolean;
  /** Fetch-in-flight for a side indicator; do not wire to DataGrid isLoading (that unmounts the table). */
  isFetching: boolean;
  users: AdminUserRow[];
  showDeleted: boolean;
  lastFetchFilters: AdminUsersServerFilters;
  /** True when last fetch returned fewer than ADMIN_USERS_SEARCH_LIMIT rows. */
  lastFetchWasComplete: boolean;
  lastFetchIncludedDeleted: boolean;
  editingUser: Tables<'users'> | null;
  changeRoleModalOpen: boolean;
  featureAccessModalOpen: boolean;
  featureAccessUser: Tables<'users'> | null;
  load: (params?: AdminUsersLoadParams) => void;
  /** silent: true skips `loading` so DataGrid doesn't unmount an open filter popover mid-keystroke. */
  search: (options?: { silent?: boolean }) => Promise<void>;
  /** Full replace only — a partial merge could desync the grid from the server. */
  setServerFilters: (next: AdminUsersServerFilters) => Promise<void>;
  /** Turning on always refetches (no deleted rows loaded); turning off is a client-only hide. */
  setShowDeleted: (showDeleted: boolean) => Promise<void>;
  impersonateUser: (id: string, user?: Tables<'users'>) => Promise<void>;
  deleteUser: (id: string) => void;
  openChangeRoleModal: (user: Tables<'users'>) => void;
  changeRole: (onSuccess?: () => void) => Promise<void>;
  openFeatureAccessModal: (user: Tables<'users'>) => void;
  closeFeatureAccessModal: () => void;
  setField: SetField<AdminUsersStore>;
  selectedRole: NonNullable<Tables<'users'>['user_role']>;
  changeRoleReason: string;
};

const defaultState = {
  // set() merges, so orgId: undefined is required or All Users keeps the last org visited.
  orgId: undefined,
  loading: true,
  isFetching: false,
  editingUser: null,
  changeRoleModalOpen: false,
  featureAccessModalOpen: false,
  featureAccessUser: null,
  selectedRole: 'teacher' as const,
  changeRoleReason: '',
  users: [],
  showDeleted: false,
  ...EMPTY_SERVER_FILTERS,
  lastFetchFilters: EMPTY_SERVER_FILTERS,
  lastFetchWasComplete: false,
  lastFetchIncludedDeleted: false,
};

export const createAdminUsersStoreSlice = createStoreSlice('AdminUsersStoreData', defaultState, ({ get, set, setField }) => {
  // Drop results from a slower, superseded search() so they cannot overwrite a later one.
  let latestSearchId = 0;

  return {
    setField,
    load: (params = {}) => {
      set({ ...defaultState, ...params });
      void get().search();
    },
    search: async ({ silent = false } = {}) => {
      const searchId = ++latestSearchId;
      const state = get();
      const { orgId, showDeleted } = state;
      const filters = pickServerFilters(state);
      if (!silent) set({ loading: true });
      set({ isFetching: true });

      const params = new URLSearchParams();
      if (orgId) params.set('orgId', orgId);
      if (filters.idFilter) params.set('id', filters.idFilter);
      if (filters.nameFilter) params.set('name', filters.nameFilter);
      if (filters.emailFilter) params.set('email', filters.emailFilter);
      if (filters.orgFilter) params.set('org', filters.orgFilter);
      if (filters.planFilter.length) params.set('plan', filters.planFilter.join(','));
      if (filters.roleFilter.length) params.set('role', filters.roleFilter.join(','));
      if (filters.statusFilter.length) params.set('status', filters.statusFilter.join(','));
      if (filters.institutionalRoleFilter.length) params.set('institutionalRole', filters.institutionalRoleFilter.join(','));
      if (showDeleted) params.set('includeDeleted', 'true');
      const queryString = params.toString();
      const url = queryString ? `/api/admin/users/search?${queryString}` : '/api/admin/users/search';

      try {
        const response = await fetch<{ users: AdminUsersStore['users'] }>(url);
        // Failures are already toasted; they have no users payload, so leave the last good list.
        if (!response.ok) return;
        const { users } = await response.json();
        if (searchId !== latestSearchId) return;

        set({
          users,
          lastFetchFilters: filters,
          lastFetchWasComplete: users.length < ADMIN_USERS_SEARCH_LIMIT,
          lastFetchIncludedDeleted: showDeleted,
        });
      } finally {
        if (searchId === latestSearchId) set({ loading: false, isFetching: false });
      }
    },
    setServerFilters: async (next) => {
      const { lastFetchFilters, lastFetchWasComplete, lastFetchIncludedDeleted, showDeleted, isFetching, ...current } = get();
      const currentFilters = pickServerFilters(current);

      // Identical patch is a no-op so a remounted popover doesn't refetch a capped unfiltered load.
      if (deepEquals(currentFilters, next)) return;

      set(next);

      // Don't skip while isFetching: lastFetchFilters is last-landed, so a race could skip wrongly.
      if (!isFetching && canSkipServerRefetch(lastFetchFilters, lastFetchWasComplete, next, lastFetchIncludedDeleted, showDeleted)) return;

      await get().search({ silent: true });
    },
    setShowDeleted: async (showDeleted) => {
      const { lastFetchIncludedDeleted } = get();
      set({ showDeleted });

      // Also refetch while isFetching: lastFetchIncludedDeleted is last-landed, so an in-flight fetch might omit deleted rows.
      if (showDeleted && (!lastFetchIncludedDeleted || get().isFetching)) await get().search({ silent: true });
    },
    impersonateUser: async (id, user) => {
      const originalAuthCookies = getAllCookies();
      for (const [key, value] of Object.entries(originalAuthCookies)) {
        const newCookieName = `original-${key}`;
        storage.setItem(newCookieName, value);
      }

      await fetch(`/api/admin/users/${id}/impersonate`, {
        method: 'POST',
        body: JSON.stringify({ returnUrl: `${window.location.pathname}${window.location.search}` }),
        onSuccess: ({ router }) => {
          if (user) {
            ampli.internalAdminUserImpersonated({
              user_impacted_email: user.email ?? '',
              organization_id: user.org_id ?? '',
              user_impacted_plan: String(user.plan) as '1' | '2' | '3',
              user_impacted_role: user.user_role!,
              user_impacted_status: user.status,
            });
          }
          router.push('/');
          router.refresh();
        },
      });
    },
    deleteUser: (id) => {
      const updatedUsers = get().users.filter((user) => user.id !== id);
      set({ users: updatedUsers });
    },
    openChangeRoleModal: (user) => {
      set({ editingUser: user, changeRoleModalOpen: true, selectedRole: user.user_role || 'teacher', changeRoleReason: '' });
    },
    openFeatureAccessModal: (user) => {
      set({ featureAccessUser: user, featureAccessModalOpen: true });
    },
    closeFeatureAccessModal: () => {
      set({ featureAccessUser: null, featureAccessModalOpen: false });
    },
    changeRole: async (onSuccess) => {
      const { editingUser, selectedRole, changeRoleReason } = get();
      if (!editingUser || !changeRoleReason.trim()) return;
      await fetch<UpdateUserRoleResponse>(`/api/admin/users/${editingUser.id}/update_role`, {
        method: 'PUT',
        body: JSON.stringify({ user_role: selectedRole, reason: changeRoleReason.trim() }),
        // The route answers refusals with plain-text messages; the global handlers would replace them with generic toasts or nothing.
        responseErrorHandlers: {
          badRequest: showRouteMessage,
          forbidden: showRouteMessage,
          notFound: showRouteMessage,
          conflict: showRouteMessage,
        },
        onSuccess: async ({ response }) => {
          const data = await response.json();
          ampli.internalAdminUserRoleUpdated({
            user_impacted_email: editingUser.email ?? '',
            organization_id: editingUser.org_id ?? '',
            user_impacted_plan: String(editingUser.plan) as '1' | '2' | '3',
            user_impacted_status: editingUser.status,
            previous_user_impacted_role: editingUser.user_role!,
            updated_user_impacted_role: selectedRole,
          });
          toast({ title: "User's role updated successfully", status: 'success' });
          set((state) => {
            const users = state.users.map((user) => (user.id === data.user?.id ? { ...user, ...data.user } : user));
            return {
              ...state,
              users,
              editingUser: null,
              changeRoleModalOpen: false,
              selectedRole: 'teacher' as const,
              changeRoleReason: '',
            };
          });
          onSuccess?.();
        },
      });
    },
  };
});
